import * as React from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { ProposalHcpJobsDto } from '@lib/dto/hcp-job.dto'
import type { HcpJobKind } from '@lib/enums/hcp-job-kind.enum'
import { proposalHcpJobService } from '@/services/ProposalHcpJobService'
import { proposalKeys } from '@/hooks/queryKeys'

/**
 * The Housecall Pro jobs an accepted version becomes. Asked for only where
 * it can mean something: an accepted version, on a deployment connected to
 * HCP.
 */
export function useProposalHcpJobs(
  versionId: string,
  enabled: boolean,
): UseQueryResult<ProposalHcpJobsDto> {
  return useQuery({
    queryKey: proposalKeys.hcpJobs(versionId),
    queryFn: () => proposalHcpJobService.preview(versionId),
    enabled,
  })
}

/**
 * The version a mutation was started for.
 *
 * TanStack Query replaces a pending mutation's callbacks on re-render, so a
 * closure over `versionId` would write version A's jobs into version B's
 * cache. The id is copied into the variables at `mutate`.
 */
function useVersionAtCall(versionId: string): React.RefObject<string> {
  const versionRef = React.useRef(versionId)
  React.useLayoutEffect(() => {
    versionRef.current = versionId
  }, [versionId])
  return versionRef
}

function rememberJobs(
  queryClient: ReturnType<typeof useQueryClient>,
  versionId: string,
  jobs: ProposalHcpJobsDto | undefined,
): void {
  if (jobs !== undefined) {
    queryClient.setQueryData(proposalKeys.hcpJobs(versionId), jobs)
  }
  void queryClient.invalidateQueries({
    queryKey: proposalKeys.hcpJobs(versionId),
  })
}

interface CreateHcpJobVariables {
  siteId: string
  kind: HcpJobKind
}

/** Creates a job in HCP's live account. Never retried. */
export function useCreateHcpJob(versionId: string) {
  const queryClient = useQueryClient()
  const versionRef = useVersionAtCall(versionId)
  const mutation = useMutation<
    ProposalHcpJobsDto,
    Error,
    CreateHcpJobVariables & { versionId: string }
  >({
    mutationFn: ({ versionId: savedVersion, siteId, kind }) =>
      proposalHcpJobService.create(savedVersion, { siteId, kind }),
    onSuccess: (jobs, variables) => {
      rememberJobs(queryClient, variables.versionId, jobs)
    },
    onSettled: (jobs, _error, variables) => {
      if (jobs !== undefined) return
      rememberJobs(queryClient, variables.versionId, undefined)
    },
  })
  return {
    ...mutation,
    mutate: (input: CreateHcpJobVariables) => {
      mutation.mutate({ ...input, versionId: versionRef.current })
    },
    mutateAsync: (input: CreateHcpJobVariables) =>
      mutation.mutateAsync({ ...input, versionId: versionRef.current }),
  }
}

interface SettleHcpJobVariables {
  recordId: string
  /** The job a person found in HCP, or null when it is not there. */
  hcpJobId: string | null
}

export function useSettleHcpJob(versionId: string) {
  const queryClient = useQueryClient()
  const versionRef = useVersionAtCall(versionId)
  const mutation = useMutation<
    ProposalHcpJobsDto,
    Error,
    SettleHcpJobVariables & { versionId: string }
  >({
    mutationFn: ({ versionId: savedVersion, recordId, hcpJobId }) =>
      proposalHcpJobService.settle(savedVersion, recordId, { hcpJobId }),
    onSuccess: (jobs, variables) => {
      rememberJobs(queryClient, variables.versionId, jobs)
    },
    onSettled: (jobs, _error, variables) => {
      if (jobs !== undefined) return
      rememberJobs(queryClient, variables.versionId, undefined)
    },
  })
  return {
    ...mutation,
    mutate: (input: SettleHcpJobVariables) => {
      mutation.mutate({ ...input, versionId: versionRef.current })
    },
    mutateAsync: (input: SettleHcpJobVariables) =>
      mutation.mutateAsync({ ...input, versionId: versionRef.current }),
  }
}

